/**
 * Hook to fetch care-time Observations whose focus is a given Task.
 * Feeds the unified progress + care-time history in the activity wizards.
 * @agentic connections
 */

import { useEffect, useReducer } from "react";
import { useMedplum } from "@medplum/react";
import type { Observation } from "@medplum/fhirtypes";
import { getCareTimeForTask } from "@benchmark/core/services";

/**
 * Return type for useTaskCareTimeLogs.
 * @agentic connections
 */
type UseTaskCareTimeLogsResult = {
  logs: Observation[];
  count: number;
  loading: boolean;
};

type State = {
  logs: Observation[];
  loading: boolean;
};

type Action =
  | { type: "start" }
  | { type: "success"; logs: Observation[] }
  | { type: "error" }
  | { type: "reset" };

const reducer = (_state: State, action: Action): State => {
  switch (action.type) {
    case "start":
      return { logs: [], loading: true };
    case "success":
      return { logs: action.logs, loading: false };
    case "error":
      return { logs: [], loading: false };
    case "reset":
      return { logs: [], loading: false };
  }
};

/**
 * Fetches active care-time Observations linked to a Task via `focus`.
 * Degrades to an empty list on error so the caller can still render the
 * Communication-only history.
 * @agentic connections
 */
export const useTaskCareTimeLogs = (
  taskId: string | undefined,
): UseTaskCareTimeLogsResult => {
  const medplum = useMedplum();
  const [state, dispatch] = useReducer(reducer, { logs: [], loading: false });

  useEffect(() => {
    if (!taskId) {
      dispatch({ type: "reset" });
      return;
    }

    let cancelled = false;
    dispatch({ type: "start" });

    getCareTimeForTask(medplum, taskId)
      .then((result) => {
        if (!cancelled) {
          dispatch({ type: "success", logs: result });
        }
      })
      .catch(() => {
        if (!cancelled) {
          dispatch({ type: "error" });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, taskId]);

  return { logs: state.logs, count: state.logs.length, loading: state.loading };
};
